import { useState, useEffect, useCallback } from 'react';
import { useSignalREvent } from './useCommunicationHub';
import { Notification, NotificationType } from '../types/communication';

const DISMISSED_POPUPS_KEY = 'scholar_minds_dismissed_popups';

// Get dismissed popup IDs from localStorage
const getDismissedPopups = (): string[] => {
  try {
    const stored = localStorage.getItem(DISMISSED_POPUPS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Add popup ID to dismissed list
const addDismissedPopup = (id: string) => {
  const dismissed = getDismissedPopups();
  if (!dismissed.includes(id)) {
    dismissed.push(id);
    localStorage.setItem(DISMISSED_POPUPS_KEY, JSON.stringify(dismissed));
  }
};

export const useEmergencyPopups = () => {
  const [currentPopup, setCurrentPopup] = useState<Notification | null>(null);
  const [popupQueue, setPopupQueue] = useState<Notification[]>([]);

  // Listen for emergency popup events from SignalR
  const handleEmergencyPopup = useCallback((popupData: any) => {
    console.log('[useEmergencyPopups] Received emergency popup:', popupData);

    const dismissed = getDismissedPopups();
    
    // Check if already dismissed
    if (dismissed.includes(popupData.id)) {
      console.log('[useEmergencyPopups] Popup already dismissed');
      return;
    }

    // Convert backend data to Notification format
    const notification: Notification = {
      id: popupData.id || popupData.notificationId,
      userId: popupData.userId || '',
      type: NotificationType.ANNOUNCEMENT,
      title: popupData.title,
      message: popupData.message || popupData.body || popupData.content,
      isRead: false,
      priority: popupData.priority || 'URGENT',
      createdAt: popupData.createdAt || new Date().toISOString(),
      metadata: {
        showAsPopup: true,
        sender: popupData.sender || popupData.senderName,
        category: popupData.category || 'Emergency',
        ...popupData.metadata,
      },
    };

    // Add to queue
    setPopupQueue((prev) => {
      // Avoid duplicates
      if (prev.some((p) => p.id === notification.id)) {
        return prev;
      }
      return [...prev, notification];
    });

    // Show immediately if no current popup
    setCurrentPopup((current) => {
      if (!current) {
        return notification;
      }
      return current;
    });
  }, []);

  // Subscribe to SignalR event
  useSignalREvent('OnEmergencyPopup', handleEmergencyPopup);

  const dismissCurrentPopup = (notificationId: string, dontShowAgain = false) => {
    if (dontShowAgain) {
      addDismissedPopup(notificationId);
    }

    // Remove from queue
    const remaining = popupQueue.filter((p) => p.id !== notificationId);
    setPopupQueue(remaining);

    // Show next popup if available
    if (remaining.length > 0) {
      setCurrentPopup(remaining[0]);
    } else {
      setCurrentPopup(null);
    }
  };

  return {
    currentPopup,
    popupQueue,
    dismissCurrentPopup,
    hasPopups: popupQueue.length > 0,
  };
};
